import { cloneDeep, isEqual } from "lodash";
import type { LLMCLassifierResponse, LLMResultDefinition } from "models/LLMs";
import { LLMClassifierImplementationConfigurationType } from "models/LLMs/LLMClassifierImplementation";
import type {
    LLMClassifierImplementationConfigurationLogicChain,
    LLMClassifierImplementationConfigurationPromptOnly,
} from "models/LLMs/LLMClassifierImplementation";
import { ModelVisibility } from "models/LLMs/ModelVisibility";
import {
    partitionExecuteConditions,
    stripVolatileNestedFields,
} from "./llmClassifierConditionGrouping";
import { deepCamelCase } from "../utils";

export interface LLMClassifierSaveChangeSection {
    title: string;
    changes: string[];
}

export interface LLMClassifierSaveChanges {
    hasChanges: boolean;
    hasGroupChange: boolean;
    oldJson: string;
    newJson: string;
    sections: LLMClassifierSaveChangeSection[];
}

export interface LLMClassifierSaveChangesOptions {
    roleNameById?: Record<string, string>;
    /** Supplied by the chain editor, since the classifier response omits them. */
    parentModels?: { before: string[]; after: string[] };
    /** Spells out both values for long prose fields. For the review dialog, not the banner. */
    includeLongTextValues?: boolean;
}

interface NormalizedNameRef {
    id?: string;
    name?: string;
}

interface NormalizedResultDefinition {
    result: string;
    criteria?: string;
    examples: string[];
    color?: string;
}

interface NormalizedLLMClassifier {
    modelDetails: {
        name?: string;
        description?: string;
        classifierGroup?: NormalizedNameRef;
        classifierCategory?: NormalizedNameRef;
        visibility: ModelVisibility;
        roleIds: string[];
        classifierOutputs: string[];
    };
    executionSetup: {
        llmModel?: NormalizedNameRef;
        purpose?: string;
        builderType?: LLMClassifierImplementationConfigurationType;
        targetSpeaker?: string;
        /**
         * Execute conditions are one saved list, but the metadata filters on the
         * volume control step and the run conditions in chain source own separate
         * parts of it, so they are grouped with the section that owns them.
         */
        metadataFilters: unknown;
    };
    standardBuilder?: {
        promptTemplate?: string;
        questionText?: string;
        questionDescription?: string;
        resultDefinitions: NormalizedResultDefinition[];
    };
    logicChainBuilder?: {
        steps: unknown[];
    };
    volumeControls: {
        quotas: unknown[];
    };
    /** The chain source section of the builder. */
    chainSource: {
        parentModels: string[];
        runConditions: unknown;
    };
}

const emptyValue = "None";

export function cloneLLMClassifierSaveSnapshot(
    classifier: LLMCLassifierResponse,
): LLMCLassifierResponse {
    return cloneDeep(classifier);
}

export function getLLMClassifierSaveChanges(
    before: LLMCLassifierResponse | undefined,
    after: LLMCLassifierResponse | undefined,
    options: LLMClassifierSaveChangesOptions = {},
): LLMClassifierSaveChanges | undefined {
    if (!before || !after) return undefined;

    const oldValue = normalizeLLMClassifierForSaveDiff(before);
    const newValue = normalizeLLMClassifierForSaveDiff(after);
    oldValue.chainSource.parentModels = options.parentModels?.before ?? [];
    newValue.chainSource.parentModels = options.parentModels?.after ?? [];
    const sections = buildChangeSections(oldValue, newValue, options);

    return {
        hasChanges: sections.some((section) => section.changes.length > 0),
        hasGroupChange: !isEqual(
            oldValue.modelDetails.classifierGroup,
            newValue.modelDetails.classifierGroup,
        ),
        oldJson: JSON.stringify(oldValue, null, 2),
        newJson: JSON.stringify(newValue, null, 2),
        sections,
    };
}

function normalizeLLMClassifierForSaveDiff(
    classifier: LLMCLassifierResponse,
): NormalizedLLMClassifier {
    const implementation = classifier.classifierImplementation;
    const configuration = implementation.configuration;
    const partitioned = partitionExecuteConditions(
        configuration.executeConditions,
    );
    const metadataFilters = stripVolatileNestedFields(
        partitioned.metadataFilters,
    );
    const runConditions = stripVolatileNestedFields(partitioned.runConditions);

    return {
        modelDetails: {
            name: classifier.name,
            description: classifier.description ?? "",
            classifierGroup: normalizeNameRef(
                classifier.classifierGroupId,
                classifier.classifierGroup?.name,
            ),
            classifierCategory: normalizeNameRef(
                classifier.classifierCategoryId,
                classifier.classifierCategory?.name,
            ),
            visibility: getVisibility(classifier),
            roleIds: (classifier.rolesAccessControls ?? [])
                .map((control) => control.roleId)
                .sort(),
            classifierOutputs: (classifier.classifierOutputs ?? [])
                .map((output) => output.result)
                .sort(),
        },
        executionSetup: {
            llmModel: normalizeNameRef(
                implementation.llmModelId,
                implementation.llmModel?.name,
            ),
            purpose: implementation.llmPurpose?.toString(),
            builderType: implementation.type,
            targetSpeaker: implementation.llmTargetSpeaker ?? "",
            metadataFilters,
        },
        standardBuilder:
            implementation.type ===
            LLMClassifierImplementationConfigurationType.PromptOnly
                ? normalizeStandardBuilder(
                      configuration as LLMClassifierImplementationConfigurationPromptOnly,
                      implementation.llmResultDefinitions ?? [],
                  )
                : undefined,
        logicChainBuilder:
            implementation.type ===
            LLMClassifierImplementationConfigurationType.LogicChaining
                ? normalizeLogicChainBuilder(
                      configuration as LLMClassifierImplementationConfigurationLogicChain,
                  )
                : undefined,
        chainSource: {
            parentModels: [],
            runConditions,
        },
        volumeControls: {
            quotas: normalizeQuotas(
                classifier.modelComputeOption?.modelComputeQuotas ?? [],
            ),
        },
    };
}

function normalizeStandardBuilder(
    configuration: LLMClassifierImplementationConfigurationPromptOnly,
    resultDefinitions: LLMResultDefinition[],
): NormalizedLLMClassifier["standardBuilder"] {
    return {
        promptTemplate: configuration.promptTemplate,
        questionText: configuration.questionText,
        questionDescription: configuration.questionDescription,
        resultDefinitions: normalizeResultDefinitions(resultDefinitions),
    };
}

function normalizeLogicChainBuilder(
    configuration: LLMClassifierImplementationConfigurationLogicChain,
): NormalizedLLMClassifier["logicChainBuilder"] {
    // Steps arrive Pascal-cased for the wire format (LLMClassifierLogicChainBuilder.
    // getConfiguration); camel-case them back so the strip below and toStepSummary read the
    // right keys instead of silently matching nothing.
    const steps = deepCamelCase(configuration.steps ?? [], [
        "inputVariables",
        "structuredOutputJsonSchema",
    ]) as unknown[];

    return {
        steps: steps.map((step) => stripVolatileNestedFields(step)),
    };
}

function buildChangeSections(
    before: NormalizedLLMClassifier,
    after: NormalizedLLMClassifier,
    options: LLMClassifierSaveChangesOptions,
): LLMClassifierSaveChangeSection[] {
    return [
        {
            title: "Model details",
            changes: [
                changedText(
                    "Name",
                    before.modelDetails.name,
                    after.modelDetails.name,
                ),
                changedLongText(
                    "Description",
                    before.modelDetails.description,
                    after.modelDetails.description,
                    options,
                ),
                changedNameRef(
                    "Model group",
                    before.modelDetails.classifierGroup,
                    after.modelDetails.classifierGroup,
                ),
                changedNameRef(
                    "Category",
                    before.modelDetails.classifierCategory,
                    after.modelDetails.classifierCategory,
                ),
                changedText(
                    "Visibility",
                    formatVisibility(before.modelDetails, options),
                    formatVisibility(after.modelDetails, options),
                ),
                changedList(
                    "Model outputs",
                    before.modelDetails.classifierOutputs,
                    after.modelDetails.classifierOutputs,
                ),
            ],
        },
        {
            title: "Execution setup",
            changes: [
                changedNameRef(
                    "LLM model",
                    before.executionSetup.llmModel,
                    after.executionSetup.llmModel,
                ),
                changedText(
                    "Purpose",
                    before.executionSetup.purpose,
                    after.executionSetup.purpose,
                ),
                changedText(
                    "Builder",
                    formatBuilderType(before.executionSetup.builderType),
                    formatBuilderType(after.executionSetup.builderType),
                ),
                changedText(
                    "Target speaker",
                    before.executionSetup.targetSpeaker,
                    after.executionSetup.targetSpeaker,
                ),
                changedComplex(
                    "Metadata filters changed",
                    before.executionSetup.metadataFilters,
                    after.executionSetup.metadataFilters,
                ),
            ],
        },
        {
            title: "Chain source",
            changes: [
                changedList(
                    "Parent models",
                    before.chainSource.parentModels,
                    after.chainSource.parentModels,
                ),
                changedComplex(
                    "Run conditions changed",
                    before.chainSource.runConditions,
                    after.chainSource.runConditions,
                ),
            ],
        },
        {
            title: "Standard builder",
            changes: buildStandardBuilderChanges(
                before.standardBuilder,
                after.standardBuilder,
                options,
            ),
        },
        {
            title: "Logic chain builder",
            changes: buildLogicChainChanges(
                before.logicChainBuilder,
                after.logicChainBuilder,
            ),
        },
        {
            title: "Volume controls",
            changes: [
                changedComplex(
                    `Hierarchy rules changed (${before.volumeControls.quotas.length} to ${after.volumeControls.quotas.length})`,
                    before.volumeControls.quotas,
                    after.volumeControls.quotas,
                ),
            ],
        },
    ]
        .map((section) => ({
            ...section,
            changes: section.changes.filter(Boolean) as string[],
        }))
        .filter((section) => section.changes.length > 0);
}

function buildStandardBuilderChanges(
    before: NormalizedLLMClassifier["standardBuilder"],
    after: NormalizedLLMClassifier["standardBuilder"],
    options: LLMClassifierSaveChangesOptions,
): string[] {
    if (!before && !after) return [];
    if (!before || !after) return ["Standard builder configuration changed"];

    return [
        changedLongText(
            "Prompt template",
            before.promptTemplate,
            after.promptTemplate,
            options,
        ),
        changedLongText(
            "Question text",
            before.questionText,
            after.questionText,
            options,
        ),
        changedLongText(
            "Question description",
            before.questionDescription,
            after.questionDescription,
            options,
        ),
        ...buildResultDefinitionChanges(
            before.resultDefinitions,
            after.resultDefinitions,
        ),
    ].filter(Boolean) as string[];
}

function buildResultDefinitionChanges(
    before: NormalizedResultDefinition[],
    after: NormalizedResultDefinition[],
): string[] {
    const changes: string[] = [];
    const beforeByResult = new Map(
        before.map((definition) => [definition.result, definition]),
    );
    const afterByResult = new Map(
        after.map((definition) => [definition.result, definition]),
    );
    const removed = before.filter(
        (definition) => !afterByResult.has(definition.result),
    );
    const added = after.filter(
        (definition) => !beforeByResult.has(definition.result),
    );

    if (added.length > 0) {
        changes.push(
            `Result definitions added: ${added
                .map((definition) => definition.result)
                .join(", ")}`,
        );
    }

    if (removed.length > 0) {
        changes.push(
            `Result definitions removed: ${removed
                .map((definition) => definition.result)
                .join(", ")}`,
        );
    }

    for (const [result, oldDefinition] of beforeByResult) {
        const newDefinition = afterByResult.get(result);
        if (!newDefinition) continue;

        if (oldDefinition.criteria !== newDefinition.criteria) {
            changes.push(`Criteria changed for "${result}"`);
        }

        if (!isEqual(oldDefinition.examples, newDefinition.examples)) {
            changes.push(`Examples changed for "${result}"`);
        }

        if (oldDefinition.color !== newDefinition.color) {
            changes.push(`Color changed for "${result}"`);
        }
    }

    return changes;
}

function buildLogicChainChanges(
    before: NormalizedLLMClassifier["logicChainBuilder"],
    after: NormalizedLLMClassifier["logicChainBuilder"],
): string[] {
    if (!before && !after) return [];
    if (!before || !after) return ["Logic chain configuration changed"];

    const changes: string[] = [];
    const beforeSteps = before.steps.map(toStepSummary);
    const afterSteps = after.steps.map(toStepSummary);
    const beforeById = new Map(
        beforeSteps.map((step, index) => [step.id || `index-${index}`, step]),
    );
    const afterById = new Map(
        afterSteps.map((step, index) => [step.id || `index-${index}`, step]),
    );
    const added = afterSteps.filter(
        (step, index) => !beforeById.has(step.id || `index-${index}`),
    );
    const removed = beforeSteps.filter(
        (step, index) => !afterById.has(step.id || `index-${index}`),
    );

    if (added.length > 0) {
        changes.push(
            `Steps added: ${added.map((step) => step.name).join(", ")}`,
        );
    }

    if (removed.length > 0) {
        changes.push(
            `Steps removed: ${removed.map((step) => step.name).join(", ")}`,
        );
    }

    beforeSteps.forEach((oldStep, index) => {
        const stepKey = oldStep.id || `index-${index}`;
        const newStep = afterById.get(stepKey) ?? afterSteps[index];
        if (!newStep) return;

        if (oldStep.name !== newStep.name) {
            changes.push(
                `Step renamed: ${formatValue(oldStep.name)} to ${formatValue(
                    newStep.name,
                )}`,
            );
        } else if (!isEqual(oldStep.raw, newStep.raw)) {
            changes.push(`Step changed: ${formatValue(oldStep.name)}`);
        }
    });

    if (changes.length === 0 && !isEqual(before.steps, after.steps)) {
        changes.push("Logic chain steps changed");
    }

    return changes;
}

function normalizeNameRef(
    id?: string,
    name?: string,
): NormalizedNameRef | undefined {
    if (!id && !name) return undefined;
    return { id, name };
}

function normalizeResultDefinitions(
    definitions: LLMResultDefinition[],
): NormalizedResultDefinition[] {
    return definitions
        .map((definition) => ({
            result: definition.result,
            criteria: definition.criteria,
            examples: [...(definition.examples ?? [])],
            color: definition.color,
        }))
        .sort((a, b) => a.result.localeCompare(b.result));
}

function normalizeQuotas(quotas: unknown[]): unknown[] {
    return quotas
        .map((quota) => stripVolatileNestedFields(quota, ["id"]))
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
}

// The fallback mirrors LLMClassifierLogicChainBuilder.getNextStepName's own "Step N" convention,
// which is what a step without a custom name shows in the builder itself.
function toStepSummary(step: unknown, index: number) {
    const record = step as Record<string, unknown>;
    return {
        id: record.id as string | undefined,
        name: (record.name as string | undefined) || `Step ${index + 1}`,
        raw: step,
    };
}

function getVisibility(classifier: LLMCLassifierResponse): ModelVisibility {
    if (classifier.isInternal) return ModelVisibility.AuthenticxOnly;
    if ((classifier.rolesAccessControls ?? []).length > 0) {
        return ModelVisibility.SpecificRoles;
    }
    return ModelVisibility.Anyone;
}

function changedText(label: string, before?: string, after?: string) {
    if ((before ?? "") === (after ?? "")) return undefined;
    return `${label}: ${formatValue(before)} to ${formatValue(after)}`;
}

/** Long prose: both values swamp a summary, so only the review dialog asks for them. */
function changedLongText(
    label: string,
    before: string | undefined,
    after: string | undefined,
    options: LLMClassifierSaveChangesOptions,
) {
    if ((before ?? "") === (after ?? "")) return undefined;
    return options.includeLongTextValues
        ? `${label}: ${formatValue(before)} to ${formatValue(after)}`
        : `${label} changed`;
}

function changedNameRef(
    label: string,
    before?: NormalizedNameRef,
    after?: NormalizedNameRef,
) {
    if (isEqual(before, after)) return undefined;
    return `${label}: ${formatNameRef(before)} to ${formatNameRef(after)}`;
}

function changedList(label: string, before: string[], after: string[]) {
    if (isEqual(before, after)) return undefined;
    return `${label}: ${formatList(before)} to ${formatList(after)}`;
}

function changedComplex(label: string, before: unknown, after: unknown) {
    if (isEqual(before, after)) return undefined;
    return label;
}

function formatVisibility(
    details: NormalizedLLMClassifier["modelDetails"],
    options: LLMClassifierSaveChangesOptions,
) {
    if (details.visibility !== ModelVisibility.SpecificRoles) {
        return details.visibility;
    }

    const roles = details.roleIds.map(
        (roleId) => options.roleNameById?.[roleId] ?? roleId,
    );
    return `${ModelVisibility.SpecificRoles}: ${formatList(roles)}`;
}

function formatBuilderType(
    type?: LLMClassifierImplementationConfigurationType,
) {
    switch (type) {
        case LLMClassifierImplementationConfigurationType.PromptOnly:
            return "Standard";
        case LLMClassifierImplementationConfigurationType.LogicChaining:
            return "Advanced";
        default:
            return undefined;
    }
}

function formatNameRef(value?: NormalizedNameRef) {
    return formatValue(value?.name ?? value?.id);
}

function formatList(values: string[]) {
    if (values.length === 0) return emptyValue;
    return values.map(formatValue).join(", ");
}

function formatValue(value?: string) {
    if (!value) return emptyValue;
    const trimmed = value.trim();
    if (trimmed.length === 0) return emptyValue;
    return trimmed.length > 80 ? `${trimmed.substring(0, 77)}...` : trimmed;
}
